import React, { useState, useEffect, useMemo } from 'react';
import { Layout, Menu, Avatar, Dropdown, Button, theme, Drawer } from 'antd';
import type { MenuProps } from 'antd';
import { useQuery } from 'react-query';
import {
  ApiOutlined,
  DashboardOutlined,
  AppstoreOutlined,
  ShoppingOutlined,
  TeamOutlined,
  ShoppingCartOutlined,
  SettingOutlined,
  CreditCardOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MenuOutlined,
  CloseOutlined,
  QuestionCircleOutlined,
  FileTextOutlined,
  OrderedListOutlined,
  GoogleOutlined,
  MessageOutlined,
  SendOutlined,
  UserSwitchOutlined,
  LineChartOutlined,
  RocketOutlined,
  FormOutlined,
  TruckOutlined,
  DatabaseOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  AimOutlined,
  ClusterOutlined,
  DeleteOutlined,
  BarChartOutlined,
  StarOutlined,
  BulbOutlined,
  SearchOutlined,
  PlusOutlined,
  ThunderboltOutlined,
  ShopOutlined,
  DownOutlined,
  InboxOutlined,
  TagOutlined,
  ReadOutlined,
  BookOutlined,
  FilePdfOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useNovaPoshtaConfigured } from '../hooks/useNovaPoshtaConfigured';
import { useModuleEnabled } from '../hooks/useModuleEnabled';
import styles from './AdminLayout.module.css';
import { RobotOutlined, DollarOutlined } from '@ant-design/icons';
import AiAssistantPanel, { AgentCommand } from './agent/AiAssistantPanel';
import Brand from './Brand';
import TenantSwitcher from './TenantSwitcher';
import CommandPalette, { PaletteEntry } from './header/CommandPalette';
import { SLASH_COMMANDS } from './agent/AgentCommandPalette';
import NotificationsBell from './header/NotificationsBell';
import { HeaderActionsContext } from '../contexts/HeaderActionsContext';
import { agentAPI } from '../services/agentApi';
import { contextAPI } from '../services/api';
import { CLIENT_BRAND } from '../config/brand';
import type { AgentConversationSummary } from '../types/agent';

const { Header, Sider, Content } = Layout;

const isMac = typeof navigator !== 'undefined' && /Mac|iP/.test(navigator.platform);

// Документація живе поза SPA (Netlify-проксі на /docs, окремий Next.js-сайт),
// тож це звичайне посилання в нову вкладку, а не react-router-маршрут.
const DOCS_KEY = 'docs-external';
const DOCS_URL = '/docs';

interface AdminLayoutProps {
  children: React.ReactNode;
}

interface NavPage {
  key: string;
  icon: React.ReactNode;
  label: string;
}

interface NavSection {
  subKey: string;
  icon: React.ReactNode;
  label: string;
  visible: boolean;
  pages: NavPage[];
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [headerActions, setHeaderActions] = useState<React.ReactNode>(null);
  const [pendingCmd, setPendingCmd] = useState<AgentCommand | null>(null);
  const [agentConversations, setAgentConversations] = useState<AgentConversationSummary[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasPermission } = useAuth();
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const { configured: npConfigured } = useNovaPoshtaConfigured();
  const recipesEnabled = useModuleEnabled('recipes');
  const productRequestsEnabled = useModuleEnabled('product_requests');

  // Stable context value: a fresh object every render would re-render every
  // consumer, whose useHeaderActions effect would setState here again — a loop.
  const headerActionsCtx = useMemo(() => ({ setActions: setHeaderActions }), []);

  // ⌘K / Ctrl+K opens the single unified command palette everywhere (navigation
  // + assistant actions in one field). The assistant no longer binds ⌘K.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // e.code, not e.key: on the Ukrainian layout the key is 'л', never 'k'.
      if ((e.metaKey || e.ctrlKey) && e.code === 'KeyK') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const canUseAgent = hasPermission('admin_agent.access');
  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const role = user?.role;

  // Live tenant context. The full org/platform card lives in TenantSwitcher
  // (sidebar); here the brand only serves as the page-title fallback. Fails
  // soft: on an old backend (404) or any error the query stays empty and we
  // fall back to the CLIENT_BRAND constant. Fetched once, refreshed on
  // login/logout (useAuth invalidates ['admin-context']).
  const { data: tenantContext } = useQuery(['admin-context'], contextAPI.get, {
    retry: false,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const brandLabel = tenantContext?.platform.brand_name ?? CLIENT_BRAND;

  type MenuSection = 'dashboard' | 'orders' | 'orders_clients' | 'warehouse' | 'catalog'
    | 'statistics' | 'statistics_products' | 'marketing' | 'system';

  const canSee = (section: MenuSection): boolean => {
    if (role === 'owner' || role === 'admin' || role === 'superadmin') return true;
    if (role === 'ai_agent') return ['catalog', 'statistics_products'].includes(section);
    if (role === 'manager' || role === 'warehouse_manager')
      return ['orders', 'orders_clients', 'warehouse', 'catalog'].includes(section);
    return false;
  };

  // Owner/admin (and superadmin) manage the organization: team + settings.
  const canManageOrg = role === 'owner' || role === 'admin' || role === 'superadmin';

  // OKR is a platform-operator tool. Gate it through the permission model
  // (like admin_agent.access) rather than the org role: `okr.view` is granted
  // to nobody, so only platform superadmins — who bypass permission checks in
  // hasPermission — see it. Keying off `role === 'superadmin'` would break in
  // the multi-org model, where `role` is always the active-org role.
  const canSeeOkr = hasPermission('okr.view');

  // Standalone top-level items (no submenu)
  const topItems = [
    ...(canSee('dashboard') ? [{ key: '/dashboard', icon: <DashboardOutlined />, label: 'Дашборд', title: 'Дашборд' }] : []),
    ...(canSeeOkr ? [{ key: '/okr', icon: <AimOutlined />, label: 'OKR' }] : []),
  ];

  // Collapsible accordion sections
  const sections: NavSection[] = [
    {
      subKey: 'sub-org', icon: <TeamOutlined />, label: 'Моя організація',
      visible: canManageOrg,
      pages: [
        { key: '/org/team', icon: <TeamOutlined />, label: 'Команда' },
      ],
    },
    {
      subKey: 'sub-orders', icon: <ShoppingCartOutlined />, label: 'Замовлення & Клієнти',
      visible: canSee('orders') || canSee('orders_clients'),
      pages: [
        { key: '/orders', icon: <ShoppingCartOutlined />, label: 'Замовлення' },
        ...(canSee('orders') && npConfigured ? [{ key: '/np-dispatch', icon: <SendOutlined />, label: 'Відправлення НП' }] : []),
        ...(canSee('orders_clients') ? [
          { key: '/users', icon: <TeamOutlined />, label: 'Користувачі' },
          { key: '/communication', icon: <MessageOutlined />, label: 'Повідомлення' },
        ] : []),
      ],
    },
    {
      subKey: 'sub-warehouse', icon: <DatabaseOutlined />, label: 'Склад',
      visible: canSee('warehouse'),
      pages: [
        { key: '/warehouse', icon: <DatabaseOutlined />, label: 'Залишки' },
        { key: '/deliveries', icon: <TruckOutlined />, label: 'Накладні' },
        // Route is ADMIN_SUPERADMIN-guarded (App.tsx) — keep the entry gated too.
        ...(canManageOrg
          ? [{ key: '/shelves', icon: <InboxOutlined />, label: 'Полички' }]
          : []),
      ],
    },
    {
      subKey: 'sub-catalog', icon: <ShoppingOutlined />, label: 'Каталог',
      visible: canSee('catalog'),
      pages: [
        { key: '/products', icon: <ShoppingOutlined />, label: 'Товари' },
        { key: '/categories', icon: <AppstoreOutlined />, label: 'Категорії' },
        { key: '/supercategories', icon: <ClusterOutlined />, label: 'Суперкатегорії' },
        { key: '/catalog-order', icon: <OrderedListOutlined />, label: 'Порядок каталогу' },
        ...(recipesEnabled ? [{ key: '/recipes', icon: <BookOutlined />, label: 'Рецепти' }] : []),
        { key: '/product-feed', icon: <GoogleOutlined />, label: 'Google-фід' },
      ],
    },
    {
      subKey: 'sub-statistics', icon: <LineChartOutlined />, label: 'Статистика',
      visible: canSee('statistics') || canSee('statistics_products'),
      pages: [
        ...(canSee('statistics_products') ? [{ key: '/statistics/products', icon: <LineChartOutlined />, label: 'Товари' }] : []),
        ...(canSee('statistics') ? [{ key: '/statistics/users', icon: <BarChartOutlined />, label: 'Користувачі' }] : []),
        ...(canSee('statistics') ? [{ key: '/statistics/profit', icon: <DollarOutlined />, label: 'Прибуток і маржа' }] : []),
        ...(canSee('statistics') ? [{ key: '/finance', icon: <DollarOutlined />, label: 'Фінанси (P&L)' }] : []),
        // Той самий гейт, що на бекенді (admin/superadmin): canSee('statistics')
        // повертає true лише для owner/admin/superadmin.
        ...(canSee('statistics') ? [{ key: '/payments', icon: <CreditCardOutlined />, label: 'Інвойси Monobank' }] : []),
        ...(canSee('statistics') ? [{ key: '/reports', icon: <FilePdfOutlined />, label: 'Звіти в Telegram' }] : []),
      ],
    },
    {
      subKey: 'sub-marketing', icon: <RocketOutlined />, label: 'Маркетинг',
      visible: canSee('marketing'),
      pages: [
        { key: '/campaigns', icon: <RocketOutlined />, label: 'Кампанії' },
        { key: '/promotions', icon: <TagOutlined />, label: 'Акції' },
        { key: '/broadcast', icon: <SendOutlined />, label: 'Розсилки' },
        { key: '/surveys', icon: <FormOutlined />, label: 'Опитування' },
        { key: '/reviews', icon: <StarOutlined />, label: 'Відгуки про товари' },
        ...(productRequestsEnabled
          ? [{ key: '/product-requests', icon: <BulbOutlined />, label: 'Запити товарів' }]
          : []),
        { key: '/abandoned-carts', icon: <DeleteOutlined />, label: 'Покинуті кошики' },
      ],
    },
    {
      subKey: 'sub-system', icon: <SettingOutlined />, label: 'Система',
      visible: canSee('system'),
      pages: [
        // Одні двері в налаштування магазину. Компанія, бренд і платежі були
        // трьома окремими пунктами меню, тож знайти потрібне поле означало
        // вгадати, в якому з них воно опинилось. Тепер це розділи однієї
        // сторінки, а глибокі посилання на них лишаються робочими (?tab=).
        { key: '/settings', icon: <SettingOutlined />, label: 'Налаштування магазину' },
        { key: '/faq', icon: <QuestionCircleOutlined />, label: 'Часті питання' },
        { key: '/legal', icon: <FileTextOutlined />, label: 'Документи' },
        { key: '/settings/pickup-location', icon: <EnvironmentOutlined />, label: 'Точка самовивозу' },
        { key: '/settings/delivery-schedule', icon: <CalendarOutlined />, label: 'Графік доставки' },
        { key: '/admin-users', icon: <UserSwitchOutlined />, label: 'Адміністратори магазину' },
        { key: '/mcp', icon: <ApiOutlined />, label: 'Claude / MCP' },
      ],
    },
  ];

  const visibleSections = sections.filter((s) => s.visible);

  // Flat navigation: sections render as non-clickable group headers with their
  // pages always visible — any page is one tap away (no accordion state).
  // Пункт «Документація» — окремо внизу, видимий усім ролям. Відкриває /docs у
  // новій вкладці через справжнє <a> (не react-router Link): це не SPA-маршрут.
  // onClick.stopPropagation глушить Menu-onClick, щоб не було SPA-навігації;
  // клік по іконці/паддінгу підхоплює handleMenuClick (window.open).
  const docsItem = {
    key: DOCS_KEY,
    icon: <ReadOutlined />,
    label: (
      <a
        href={DOCS_URL}
        target="_blank"
        rel="noopener"
        className={styles.docsLink}
        onClick={(e) => e.stopPropagation()}
      >
        Документація
      </a>
    ),
    title: 'Документація',
  };

  const menuItems: MenuProps['items'] = [
    ...topItems,
    ...visibleSections.map((s) => ({
      key: s.subKey,
      type: 'group' as const,
      label: s.label,
      children: s.pages.map((p) => ({ key: p.key, icon: p.icon, label: p.label, title: p.label })),
    })),
    { type: 'divider' as const },
    docsItem,
  ];

  // Which section owns the current route? (drives the header crumb dropdown)
  const activeSub = visibleSections.find((s) =>
    s.pages.some((p) => location.pathname === p.key || location.pathname.startsWith(p.key + '/'))
  );

  // Flat route → {label, section, icon} map for the header crumb + palette.
  const routePages = [
    ...topItems
      .filter((i) => i.key.startsWith('/'))
      .map((i) => ({ key: i.key, label: i.label, section: '', icon: i.icon })),
    ...visibleSections.flatMap((s) =>
      s.pages.map((p) => ({ key: p.key, label: p.label, section: s.label, icon: p.icon }))
    ),
  ];
  // Detail routes that don't prefix-match their section page (e.g. /survey/:id).
  const ROUTE_ALIASES: Array<[prefix: string, navKey: string]> = [
    ['/survey/', '/surveys'],
  ];
  const aliasKey = ROUTE_ALIASES.find(([p]) => location.pathname.startsWith(p))?.[1];
  const currentPage =
    routePages.find((p) => location.pathname === p.key) ||
    routePages.find((p) => location.pathname.startsWith(p.key + '/')) ||
    routePages.find((p) => p.key === aliasKey);

  const roleLabels: Record<string, string> = {
    superadmin: 'Суперадмін',
    owner: 'Власник',
    admin: 'Адміністратор',
    manager: 'Менеджер',
    ai_agent: 'AI-агент',
    warehouse_manager: 'Менеджер складу',
  };

  // Load recent conversations lazily when the palette opens (agent users only).
  useEffect(() => {
    if (paletteOpen && canUseAgent) {
      agentAPI.listConversations().then(setAgentConversations).catch(() => {});
    }
  }, [paletteOpen, canUseAgent]);

  // Agent actions chosen from the global palette open the AI panel and run there.
  const dispatchAgent = (cmd: AgentCommand) => {
    setPendingCmd(cmd);
    setAiPanelOpen(true);
  };

  // Unified ⌘K entries: navigation pages + (for agent users) assistant actions.
  const paletteEntries: PaletteEntry[] = [
    ...routePages
      .filter((p) => p.key.startsWith('/'))
      .map((p) => ({
        id: p.key,
        group: 'Перейти до',
        label: p.label,
        hint: p.section,
        keywords: p.section,
        icon: p.icon,
        active: p.key === currentPage?.key,
        run: () => navigate(p.key),
      })),
    ...(canUseAgent
      ? [
          {
            id: 'agent-new',
            group: 'Асистент',
            label: 'Новий чат',
            icon: <PlusOutlined />,
            run: () => dispatchAgent({ type: 'new' }),
          },
          ...SLASH_COMMANDS.map((c) => ({
            id: c.keyword,
            group: 'Асистент',
            label: c.label,
            hint: c.keyword,
            keywords: c.keyword,
            icon: <ThunderboltOutlined />,
            run: () => dispatchAgent({ type: 'prompt', prompt: c.prompt }),
          })),
          ...agentConversations.map((c) => ({
            id: `conv-${c.id}`,
            group: 'Розмови',
            label: c.title || 'Без назви',
            icon: <MessageOutlined />,
            run: () => dispatchAgent({ type: 'conversation', id: c.id }),
          })),
        ]
      : []),
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    // Документація — зовнішнє посилання: нова вкладка, без SPA-навігації. Клік
    // по тексту вже обробив <a> (stopPropagation), сюди доходять лише кліки по
    // іконці/паддінгу пункту.
    if (key === DOCS_KEY) {
      window.open(DOCS_URL, '_blank', 'noopener');
      setDrawerOpen(false);
      return;
    }
    navigate(key);
    setDrawerOpen(false);
  };

  const toggleSidebar = () => setCollapsed(!collapsed);
  const openMobileDrawer = () => setDrawerOpen(true);

  const handleLogout = async () => {
    await logout();
    // When another saved account remains, logout() already triggered a full
    // reload into it; this navigate only runs when no accounts are left.
    navigate('/login');
  };

  // Account switching moved to TenantSwitcher in the sidebar.
  const userMenuItems: MenuProps['items'] = [
    { key: 'logout', icon: <LogoutOutlined />, label: 'Вийти', onClick: handleLogout },
  ];

  // Розмітка, а не компонент, оголошений у рендері: `const SideMenu = () => …`
  // давав НОВИЙ тип компонента на кожну перемальовку AdminLayout, тож React
  // щоразу перемонтовував увесь сайдбар. Логотип (<img>) пересоздавався і
  // видимо блимав, а TenantSwitcher втрачав стан і перезапитував контекст —
  // на кожен рендер сторінки (сторінки проєктують дію в шапку щорендеру).
  const sideMenu = (
    <>
      <div className={`${styles.logo} ${collapsed ? styles.logoCollapsed : ''}`}>
        <Brand variant={collapsed ? 'mark' : 'full'} context="light" />
      </div>
      <TenantSwitcher collapsed={collapsed} />
      <Menu
        theme="light"
        mode="inline"
        selectedKeys={[location.pathname]}
        items={menuItems}
        onClick={handleMenuClick}
        className={styles.menu}
      />
    </>
  );

  const isCommunicationPage = location.pathname === '/communication';

  const contentClassName = isCommunicationPage
    ? `${styles.content} ${styles.contentBare}`
    : styles.content;

  return (
    <Layout className={styles.layout} hasSider>
      {!isCommunicationPage && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          theme="light"
          width={232}
          className={styles.sider}
        >
          {sideMenu}
        </Sider>
      )}

      <Drawer
        title={<Brand variant="full" context="light" />}
        placement="left"
        // antd renders its own close icon before the title; the brand has to
        // lead, so the close button moves into `extra` (rendered after it).
        closable={false}
        extra={
          <Button
            type="text"
            aria-label="Закрити навігацію"
            icon={<CloseOutlined />}
            onClick={() => setDrawerOpen(false)}
          />
        }
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        width={280}
        styles={{
          body: { padding: 0, backgroundColor: '#ffffff', overflowY: 'auto' },
          header: { backgroundColor: '#ffffff', borderBottom: '1px solid #f0f0f0' },
        }}
        className="mobile-nav-drawer"
      >
        <TenantSwitcher />
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
          className={styles.drawerMenu}
        />
      </Drawer>

      <Layout className={styles.innerLayout}>
        {!isCommunicationPage && (
          <Header
            className={styles.header}
            style={{ '--bg-color': colorBgContainer } as React.CSSProperties}
          >
            <Button
              type="text"
              onClick={() => {
                if (window.matchMedia('(max-width: 767px)').matches) {
                  openMobileDrawer();
                } else {
                  toggleSidebar();
                }
              }}
              aria-label="Перемкнути навігацію"
              aria-expanded={!collapsed}
              className={styles.toggleButton}
              icon={
                <>
                  <span className={styles.toggleIconDesktop}>
                    {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                  </span>
                  <span className={styles.toggleIconMobile}>
                    <MenuOutlined />
                  </span>
                </>
              }
            />

            <div className={styles.headerTitle}>
              {activeSub && currentPage?.section && (
                <Dropdown
                  trigger={['click']}
                  placement="bottomLeft"
                  menu={{
                    items: activeSub.pages.map((p) => ({
                      key: p.key,
                      icon: p.icon,
                      label: p.label,
                    })),
                    selectable: true,
                    selectedKeys: [currentPage.key],
                    onClick: ({ key }) => navigate(key),
                  }}
                >
                  <button
                    type="button"
                    className={styles.headerCrumb}
                    aria-haspopup="menu"
                  >
                    {currentPage.section}
                    <DownOutlined className={styles.crumbChevron} />
                  </button>
                </Dropdown>
              )}
              <span className={styles.headerPage}>
                {currentPage?.label ?? brandLabel}
              </span>
            </div>

            <div className={styles.headerRight}>
              {headerActions && (
                <>
                  <div className={styles.headerActions}>{headerActions}</div>
                  <span className={styles.headerDivider} aria-hidden="true" />
                </>
              )}

              <button
                type="button"
                className={styles.search}
                onClick={() => setPaletteOpen(true)}
                aria-label="Перейти до сторінки або команди"
              >
                <SearchOutlined />
                <span className={styles.searchText}>Перейти…</span>
                <kbd className={styles.kbd}>{isMac ? '⌘K' : 'Ctrl+K'}</kbd>
              </button>

              <NotificationsBell />

              <Dropdown
                menu={{ items: userMenuItems }}
                placement="bottomRight"
                trigger={['click']}
              >
                <button type="button" className={styles.userBlock}>
                  <Avatar className={styles.avatar}>
                    {user?.username?.charAt(0).toUpperCase()}
                  </Avatar>
                  <span className={styles.userMeta}>
                    <span className={styles.username}>{user?.username}</span>
                    {role && (
                      <span className={styles.userRole}>{roleLabels[role] ?? role}</span>
                    )}
                  </span>
                </button>
              </Dropdown>
            </div>
          </Header>
        )}
        <Content
          className={contentClassName}
          style={{ '--bg-color': colorBgContainer } as React.CSSProperties}
        >
          <HeaderActionsContext.Provider value={headerActionsCtx}>
            {children}
          </HeaderActionsContext.Provider>
        </Content>
      </Layout>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} entries={paletteEntries} />
      {canUseAgent && (
        <>
          {location.pathname !== '/ai' && (
            <Button
              type="primary"
              shape="round"
              size="large"
              icon={<RobotOutlined />}
              className={styles.aiFab}
              aria-label="AI Асистент"
              title="AI Асистент"
              onClick={() => setAiPanelOpen(true)}
            >
              AI
            </Button>
          )}
          <AiAssistantPanel
            open={aiPanelOpen}
            onClose={() => setAiPanelOpen(false)}
            pendingCommand={pendingCmd}
            onConsumeCommand={() => setPendingCmd(null)}
          />
        </>
      )}
    </Layout>
  );
};

export default AdminLayout;
